import React, { useEffect, useState, useRef } from "react";
import BackBtn from "../../common/backBTN";
import DatePickerPopup from "../../common/DatePickerPopup"; // 👈 new component import

function ProfitANDLoss() {
  const [bettingRecords, setBettingRecords] = useState([]);
  const [exchangeRecords, setExchangeRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [showPicker, setShowPicker] = useState(false);

  // === Fetch Data ===
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [recordsResponse, exchangeResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/betting-record`, { headers }),
          fetch(`${process.env.REACT_APP_API_URL}/api/exchange-hub`, { headers }),
        ]);

        const recordsData = recordsResponse.ok ? await recordsResponse.json() : [];
        const exchangeData = exchangeResponse.ok ? await exchangeResponse.json() : { data: [] };
        const sports = exchangeData.data.map((r) => ({ ...r, game_type: "Sports" }));

        const all = [...recordsData, ...sports];
        setBettingRecords(recordsData);
        setExchangeRecords(sports);
        setFilteredRecords(all);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // === Date Range Filtering ===
  const filterByDate = (records) => {
    const now = new Date();
    const today = new Date(now.toDateString());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const last7 = new Date(today);
    last7.setDate(today.getDate() - 6);

    return records.filter((r) => {
      const d = new Date(r.created_at);
      if (selectedRange === "Today") return d >= today;
      if (selectedRange === "Yesterday") return d >= yesterday && d < today;
      if (selectedRange === "7-days") return d >= last7;
      if (selectedRange === "Custom" && customRange.start && customRange.end) {
        const s = new Date(customRange.start);
        const e = new Date(customRange.end);
        return d >= s && d <= e;
      }
      return true;
    });
  };

  useEffect(() => {
    const all = [...bettingRecords, ...exchangeRecords];
    const filtered = filterByDate(all);
    setFilteredRecords(filtered);
  }, [selectedRange, customRange, bettingRecords, exchangeRecords]);

  // === Group by game_type ===
  const grouped = Object.values(
    filteredRecords.reduce((acc, r) => {
      const type = r.game_type || "Unknown";
      if (!acc[type]) acc[type] = { game_type: type, bet: 0, win: 0 };
      acc[type].bet += parseFloat(r.bet_amount) || 0;
      acc[type].win += parseFloat(r.winAmount) || 0;
      return acc;
    }, {})
  );

  const totalBet = grouped.reduce((a, b) => a + b.bet, 0);
  const totalWin = grouped.reduce((a, b) => a + b.win, 0);
  const totalPL = totalWin - totalBet;
  const totalExpense = totalBet - totalWin > 0 ? totalBet - totalWin : 0;

  const handleConfirmDate = (range) => {
    setCustomRange(range);
    setShowPicker(false);
    setSelectedRange("Custom");
  };

  const displayRange =
    selectedRange === "Custom" && customRange.start && customRange.end
      ? `${new Date(customRange.start).toLocaleDateString("en-GB", {
          month: "2-digit",
          day: "2-digit",
        })} - ${new Date(customRange.end).toLocaleDateString("en-GB", {
          month: "2-digit",
          day: "2-digit",
        })}`
      : selectedRange === "Today"
      ? "Today"
      : selectedRange === "Yesterday"
      ? "Yesterday"
      : selectedRange === "7-days"
      ? "Last 7 Days"
      : "Select Range";

  return (
    <div id="mc-animate-container" className="v-base v-detail-base fade-enter-done">
      <div className="time-zone-container">
        <div className="time-zone-tips">
          <svg className="am-icon am-icon-warn_0e789534 am-icon-xs warn-icon">
            <use xlinkHref="#warn_0e789534" />
          </svg>
          Reminder: The data above is based on GMT + 6
        </div>

        <div className="mc-profitLoss-record-root">
          <div className="mc-header-wrap">
            <div
              id="mc-header"
              className="mc-navbar-blue mc-profitandloss mc-default-header am-navbar am-navbar-light"
            >
              <BackBtn />
              <div className="am-navbar-title">Personal Report</div>
              <div className="am-navbar-right" />
            </div>
            <div className="am-whitespace am-whitespace-sm mc-whitespace" />
          </div>

          {/* ===== Date Filter Buttons ===== */}
          <div className="mc-filter-container" style={{ position: "relative" }}>
            <div className="am-flexbox am-flexbox-align-middle mc-trans-filter mc-filter">
              <div className="am-flexbox am-flexbox-align-middle mc-trans-filter">
                {["Today", "Yesterday", "7-days"].map((range) => (
                  <a
                    key={range}
                    role="button"
                    onClick={() => setSelectedRange(range)}
                    className={`am-button am-button-small am-button-inline ${
                      selectedRange === range
                        ? "am-button-primary am-button-icon"
                        : "am-button-ghost"
                    }`}
                  >
                    {selectedRange === range && (
                      <svg className="am-icon am-icon-check-circle-o am-icon-xxs">
                        <use xlinkHref="#check-circle-o" />
                      </svg>
                    )}
                    <span>{range === "7-days" ? "7-days" : range}</span>
                  </a>
                ))}

                <div className="flex-shrink1 filter-time-btn">
                  <a
                    role="button"
                    onClick={() => setShowPicker(true)}
                    className="am-button am-button-ghost am-button-small am-button-inline am-button-icon"
                    style={{ flex: "0 0 50%" }}
                  >
                    <svg className="am-icon am-icon-calendar_c4db3b67 am-icon-xxs">
                      <use xlinkHref="#calendar_c4db3b67" />
                    </svg>
                    <span>{displayRange}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* ===== Main Content ===== */}
          <div className="pal-records-list-root">
            <div>
              {/* ======= ALL SUMMARY ======= */}
              <div style={{ marginBottom: "0.5rem" }}>
                <div className="records-item-header">
                  <div
                    className="am-flexbox am-flexbox-dir-row am-flexbox-justify-between am-flexbox-align-middle"
                    style={{ height: "100%" }}
                  >
                    <div>
                      <span className="ph-icon">
                        <svg className="am-icon am-icon-cons_2a785ecf am-icon-md">
                          <use xlinkHref="#cons_2a785ecf" />
                        </svg>
                      </span>
                      <span className="ph-text" style={{ fontWeight: 600 }}>
                        All
                      </span>
                    </div>
                    <div className="am-flexbox am-flexbox-dir-row am-flexbox-justify-center am-flexbox-align-bottom game-amount-content">
                      <span className="time">Total P&amp;L:&nbsp;</span>
                      <span
                        className={`amount-total ${
                          totalPL >= 0 ? "win-amount" : "loss-amount"
                        }`}
                      >
                        {Math.abs(totalPL).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="records-item-content">
                  <div
                    style={{
                      padding: "0.2rem 0.5rem",
                      borderBottom: "1px solid #dedede",
                    }}
                  >
                    <div
                      className="am-flexbox am-flexbox-dir-row am-flexbox-justify-between am-flexbox-align-middle"
                      style={{ marginBottom: "0.2rem" }}
                    >
                      <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                        <span className="time">Deposit</span>
                        <span style={{ fontWeight: 700 }}>
                          {totalBet.toFixed(2)}
                        </span>
                      </div>
                      <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                        <span className="time">Bonus</span>
                        <span style={{ fontWeight: 700 }}>0.00</span>
                      </div>
                      <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                        <span className="time">Income</span>
                        <span
                          className="card-amount color-red-out"
                          style={{ fontWeight: 700, fontSize: "0.3rem" }}
                        >
                          {totalWin.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="am-flexbox am-flexbox-dir-row am-flexbox-justify-between am-flexbox-align-middle">
                      <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                        <span className="time">Withdrawal</span>
                        <span style={{ fontWeight: 700, fontSize: "0.3rem" }}>
                          {totalExpense.toFixed(2)}
                        </span>
                      </div>
                      <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                        <span className="time">Rebate</span>
                        <span style={{ fontWeight: 700 }}>0.00</span>
                      </div>
                      <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                        <span className="time">Expenses</span>
                        <span style={{ fontWeight: 700, fontSize: "0.3rem" }}>
                          {totalExpense.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ======= GROUPED BY GAME TYPE ======= */}
              {grouped.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "1rem",
                    color: "#888",
                  }}
                >
                  No data found
                </div>
              ) : (
                grouped.map((item, i) => {
                  const pl = item.win - item.bet;
                  return (
                    <div className="records-root" key={i}>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <div className="records-item-header">
                          <div
                            className="am-flexbox am-flexbox-dir-row am-flexbox-justify-between am-flexbox-align-middle"
                            style={{ height: "100%" }}
                          >
                            <div className="header-block">
                              <span className="ph-icon personal-icon">
                                <svg className="am-icon am-icon-rng_385ad810 am-icon-md">
                                  <use xlinkHref="#rng_385ad810" />
                                </svg>
                              </span>
                              <span
                                className="ph-text"
                                style={{ fontWeight: 600 }}
                              >
                                <span>{item.game_type}</span>
                              </span>
                            </div>
                            <div className="am-flexbox am-flexbox-dir-row am-flexbox-justify-center am-flexbox-align-bottom game-amount-content">
                              <span className="time">Total P&amp;L:&nbsp;</span>
                              <span
                                className={`amount-total ${
                                  pl >= 0 ? "win-amount" : "loss-amount"
                                }`}
                              >
                                {Math.abs(pl).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="records-item-content">
                          <div
                            style={{
                              padding: "0.2rem 0.5rem",
                              borderBottom: "1px solid #dedede",
                            }}
                          >
                            <div className="am-flexbox am-flexbox-dir-row am-flexbox-justify-between am-flexbox-align-middle">
                              <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                                <span className="time">Betting</span>
                                <span style={{ fontWeight: 700 }}>
                                  {item.bet.toFixed(2)}
                                </span>
                              </div>
                              <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                                <span className="time">Valid bet</span>
                                <span style={{ fontWeight: 700 }}>0.00</span>
                              </div>
                              <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                                <span className="time">Win amount</span>
                                <span className="color-green-in game-amount-title">
                                  {item.win.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            <div className="am-flexbox am-flexbox-dir-row am-flexbox-justify-between am-flexbox-align-middle">
                              <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                                <span className="time">Rebate</span>
                                <span style={{ fontWeight: 700 }}>0.00</span>
                              </div>
                              <div className="am-flexbox am-flexbox-dir-column game-amount-content">
                                <span className="time">Bonus</span>
                                <span className="color-green-in game-amount-title">
                                  0.00
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ======= Date Picker Popup ======= */}
      <DatePickerPopup
        visible={showPicker}
        onClose={() => setShowPicker(false)}
        onConfirm={handleConfirmDate}
      />
    </div>
  );
}

export default ProfitANDLoss;
